import { $k } from "../lib/format";
import { ACCENT, ACCENT2, AMBER, BG, BG2, BORDER, CARD, DARK, DIM, MID, RED } from "../theme";
import { MOCK_TODAY } from "../lib/dates";
import { useState } from "react";

// ─── TAB: DASHBOARD ───────────────────────────────────────────────────────────

// ─── REVENUE GOAL MODAL ──────────────────────────────────────────────────────

function RevenueGoalModal({ goal, onSave, onClose }) {
  const isEdit = !!(goal && goal.id);
  const [label, setLabel] = useState(goal?.label || "");
  const [periodType, setPeriodType] = useState(goal?.period_type || "ytd");
  const [target, setTarget] = useState(goal?.target_amount ? String(goal.target_amount) : "");
  const [startDate, setStartDate] = useState(goal?.start_date || "");
  const [endDate, setEndDate] = useState(goal?.end_date || "");
  const PERIODS = [{ key:"mtd",label:"This month" },{ key:"qtd",label:"This quarter" },{ key:"ytd",label:"This year" },{ key:"custom",label:"Custom" }];
  const inputStyle = { padding:"10px 14px",borderRadius:5,border:`1px solid ${BORDER}`,background:BG,fontFamily:"'DM Mono',monospace",fontSize:14,color:DARK,outline:"none",boxSizing:"border-box",width:"100%" };

  return (
    <div className="r-overlay" style={{ position:"fixed",inset:0,background:"rgba(44,36,22,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center" }} onClick={onClose}>
      <div className="r-sheet r-sheet-pad" style={{ background:CARD,borderRadius:10,padding:"32px 36px",maxWidth:440,width:"100%",boxShadow:"0 12px 40px rgba(44,36,22,0.2)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:22 }}>
          <div style={{ fontFamily:"'Lora',serif",fontSize:20,fontWeight:600,color:DARK }}>{isEdit?"Edit revenue goal":"New revenue goal"}</div>
          <button className="r-close" aria-label="Close" onClick={onClose} style={{ background:"none",border:"none",cursor:"pointer",fontSize:18,color:DIM,padding:"0 4px",lineHeight:1 }}>×</button>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:MID,fontWeight:500,display:"block",marginBottom:8 }}>Period</label>
          <div className="r-cols-4form" style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8 }}>
            {PERIODS.map(({key,label:lbl})=>(
              <button key={key} onClick={()=>setPeriodType(key)} style={{ cursor:"pointer",padding:"10px 0",fontSize:12,fontWeight:500,fontFamily:"'DM Sans',sans-serif",borderRadius:6,border:`1.5px solid ${periodType===key?ACCENT2:BORDER}`,background:periodType===key?`${ACCENT2}18`:CARD,color:periodType===key?ACCENT2:MID,transition:"all 0.15s" }}>{lbl}</button>
            ))}
          </div>
        </div>
        {periodType==="custom" && (
          <div className="r-cols-form" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16 }}>
            <div>
              <label style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:MID,fontWeight:500,display:"block",marginBottom:6 }}>Start date</label>
              <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={{ ...inputStyle,fontSize:12 }}/>
            </div>
            <div>
              <label style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:MID,fontWeight:500,display:"block",marginBottom:6 }}>End date</label>
              <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} style={{ ...inputStyle,fontSize:12 }}/>
            </div>
          </div>
        )}
        <div style={{ marginBottom:16 }}>
          <label style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:MID,fontWeight:500,display:"block",marginBottom:8 }}>Target revenue</label>
          <div style={{ display:"flex",alignItems:"center",border:`1px solid ${BORDER}`,borderRadius:5,background:BG,overflow:"hidden" }}>
            <span style={{ padding:"10px 12px",fontFamily:"'DM Mono',monospace",fontSize:14,color:DIM,borderRight:`1px solid ${BORDER}`,flexShrink:0 }}>$</span>
            <input type="number" min="0" step="1000" value={target} onChange={e=>setTarget(e.target.value)} placeholder="0" style={{ ...inputStyle,border:"none",borderRadius:0,background:"transparent",paddingLeft:12 }} autoFocus/>
          </div>
        </div>
        <div style={{ marginBottom:28 }}>
          <label style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:MID,fontWeight:500,display:"block",marginBottom:8 }}>Label <span style={{ color:DIM,fontWeight:400 }}>(optional)</span></label>
          <input type="text" value={label} onChange={e=>setLabel(e.target.value)} placeholder="e.g. Summer push, Q2 stretch goal..." style={inputStyle}/>
        </div>
        <div className="r-btnrow" style={{ display:"flex",gap:10,justifyContent:"flex-end" }}>
          <button className="btn" onClick={onClose} style={{ fontSize:12,padding:"9px 18px" }}>Cancel</button>
          <button className="btn act" onClick={()=>{
            if(!target||parseFloat(target)<=0) return;
            onSave({ ...goal, label:label.trim(), period_type:periodType, target_amount:parseFloat(target), start_date:startDate||null, end_date:endDate||null });
            onClose();
          }} disabled={!target||parseFloat(target)<=0} style={{ fontSize:12,padding:"9px 22px",opacity:(!target||parseFloat(target)<=0)?0.4:1 }}>
            {isEdit?"Update goal":"Save goal"}
          </button>
        </div>
      </div>
    </div>
  );
}

function computeGoalProgress(goal, jobSummaries) {
  const now = MOCK_TODAY;
  const y = now.getFullYear(), m = now.getMonth();
  let startD, endD, elapsed = 0;
  const pt = goal.period_type;
  if (pt === "mtd") {
    startD = new Date(y,m,1); endD = new Date(y,m+1,0);
    elapsed = (now.getDate() / endD.getDate()) * 100;
  } else if (pt === "qtd") {
    const qs = Math.floor(m/3)*3;
    startD = new Date(y,qs,1); endD = new Date(y,qs+3,0);
    elapsed = ((now - startD) / (new Date(y,qs+3,1) - startD)) * 100;
  } else if (pt === "ytd") {
    startD = new Date(y,0,1); endD = new Date(y,11,31);
    elapsed = ((now - startD) / (new Date(y+1,0,1) - startD)) * 100;
  } else if (pt === "custom" && goal.start_date && goal.end_date) {
    startD = new Date(goal.start_date); endD = new Date(goal.end_date);
    elapsed = ((now - startD) / (endD - startD)) * 100;
  }
  elapsed = Math.max(0, Math.min(100, elapsed || 0));
  const revenue = (jobSummaries||[]).filter(j => {
    const jd = new Date(j.lastDate || j.firstDate || "");
    if (!jd || isNaN(jd)) return false;
    if (startD && jd < startD) return false;
    if (endD && jd > endD) return false;
    return true;
  }).reduce((s,j)=>s+j.revenue, 0);
  const pct = goal.target_amount > 0 ? Math.min((revenue / goal.target_amount) * 100, 100) : 0;
  const pace = elapsed > 0 ? (pct >= elapsed ? "on-track" : pct >= elapsed * 0.75 ? "at-risk" : "behind") : "on-track";
  const isExpired = endD && endD < now && goal.status === "active";
  return { goal, revenue, pct, elapsed, pace, isExpired };
}

function RevenueGoals({ goals, jobSummaries, onAdd, onEdit, onDelete, onMarkComplete, onArchive }) {
  const [collapsed, setCollapsed] = useState(false);
  const [menuGoalId, setMenuGoalId] = useState(null);
  const activeGoals = (goals||[]).filter(g => g.status === "active");

  if (!goals || goals.length === 0) {
    return (
      <div style={{ marginBottom:20,display:"flex",alignItems:"center",gap:10 }}>
        <button onClick={onAdd} className="btn" style={{ fontSize:11,padding:"6px 14px",borderColor:ACCENT,color:ACCENT }}>+ Revenue Goal</button>
        <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM }}>Track your progress toward a revenue target</span>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding:"16px 22px",marginBottom:20 }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:collapsed?0:14 }}>
        <div style={{ display:"flex",alignItems:"center",gap:8 }}>
          <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:600,letterSpacing:"0.1em",color:DIM,textTransform:"uppercase" }}>Revenue Goals</span>
          {activeGoals.length > 0 && <span style={{ fontFamily:"'DM Mono',monospace",fontSize:10,color:DIM,opacity:0.7 }}>({activeGoals.length} active)</span>}
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <button onClick={onAdd} style={{ background:"none",border:"none",cursor:"pointer",fontSize:11,color:ACCENT,fontFamily:"'DM Sans',sans-serif",fontWeight:500,padding:0 }}>+ Add</button>
          <button onClick={()=>setCollapsed(!collapsed)} style={{ background:"none",border:"none",cursor:"pointer",fontSize:11,color:DIM,padding:0,lineHeight:1 }}>{collapsed?"▼":"▲"}</button>
        </div>
      </div>
      {!collapsed && (
        <div style={{ display:"flex",flexDirection:"column" }}>
          {goals.map((g,idx) => {
            const { revenue, pct, elapsed, pace, isExpired } = computeGoalProgress(g, jobSummaries);
            const isDone = g.status === "complete";
            const isArchived = g.status === "archived";
            const barColor = isDone ? ACCENT2 : isExpired ? RED : pace==="on-track" ? ACCENT2 : pace==="at-risk" ? AMBER : RED;
            const paceLabel = isDone ? "Complete ✓" : isExpired ? "Missed" : pace==="on-track" ? "On track" : pace==="at-risk" ? "At risk" : "Behind";
            const paceColor = isDone ? ACCENT2 : isExpired ? RED : pace==="on-track" ? ACCENT2 : pace==="at-risk" ? AMBER : RED;
            const periodLabel = { mtd:"This month",qtd:"This quarter",ytd:"This year",custom:"Custom range" }[g.period_type] || g.period_type;
            return (
              <div key={g.id} style={{ padding:"12px 0",borderBottom:idx < goals.length-1 ? `1px solid ${BORDER}` : "none",opacity:isArchived?0.55:1 }}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8 }}>
                  <div style={{ flex:1,minWidth:0 }}>
                    <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DARK,fontWeight:500 }}>{g.label || periodLabel}</span>
                    {g.label && <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM,marginLeft:6 }}>{periodLabel}</span>}
                  </div>
                  <div style={{ display:"flex",alignItems:"center",gap:10,flexShrink:0 }}>
                    <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,fontWeight:500,color:paceColor }}>{paceLabel}</span>
                    <div style={{ position:"relative" }}>
                      <button onClick={()=>setMenuGoalId(menuGoalId===g.id?null:g.id)} style={{ background:"none",border:"none",cursor:"pointer",color:DIM,fontSize:16,padding:"0 4px",lineHeight:1 }}>⋮</button>
                      {menuGoalId===g.id && (
                        <div style={{ position:"absolute",right:0,top:24,background:CARD,border:`1px solid ${BORDER}`,borderRadius:5,boxShadow:"0 4px 16px rgba(44,36,22,0.12)",zIndex:200,minWidth:150 }} onClick={e=>e.stopPropagation()}>
                          {[
                            { label:"Edit",           fn:()=>{onEdit(g);setMenuGoalId(null);} },
                            { label:"Mark Complete",  fn:()=>{onMarkComplete(g.id);setMenuGoalId(null);}, hide:isDone },
                            { label:"Archive",        fn:()=>{onArchive(g.id);setMenuGoalId(null);}, hide:isArchived },
                            { label:"Delete",         fn:()=>{onDelete(g.id);setMenuGoalId(null);}, danger:true },
                          ].filter(i=>!i.hide).map((item,i,arr)=>(
                            <button key={i} onClick={item.fn} style={{ display:"block",width:"100%",textAlign:"left",padding:"9px 14px",background:"none",border:"none",borderBottom:i<arr.length-1?`1px solid ${BORDER}`:"none",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,color:item.danger?RED:MID }}>
                              {item.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ position:"relative",height:8,borderRadius:4,background:BG2,overflow:"hidden",marginBottom:6 }}>
                  <div style={{ position:"absolute",left:0,top:0,bottom:0,width:`${pct}%`,borderRadius:4,background:barColor,transition:"width 0.4s ease",opacity:isArchived?0.5:1 }}/>
                  {g.status==="active" && <div style={{ position:"absolute",left:`${Math.min(elapsed,100)}%`,top:-2,bottom:-2,width:2,background:DARK,opacity:0.2,borderRadius:1 }}/>}
                </div>
                <div style={{ display:"flex",justifyContent:"space-between" }}>
                  <span style={{ fontFamily:"'DM Mono',monospace",fontSize:11,fontWeight:600,color:DARK }}>{$k(revenue)}</span>
                  <span style={{ fontFamily:"'DM Mono',monospace",fontSize:11,color:DIM }}>{$k(g.target_amount)} target</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { RevenueGoalModal, RevenueGoals };
